/**
 * CADAIAnalysisPanel - AI analysis results display
 *
 * Renders Markdown-formatted analysis as styled pre-formatted text.
 */

import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from '@mui/material';
import { AutoAwesome as AIIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { analyzeCADModel } from '../../services/cad.service';

interface CADAIAnalysisPanelProps {
  modelId: string | null;
  existingAnalysis: string | null;
  onAnalysisComplete?: (analysis: string) => void;
}

/**
 * Simple Markdown-to-JSX renderer for basic formatting
 */
function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];

  lines.forEach((line, i) => {
    const trimmed = line.trim();

    if (trimmed.startsWith('### ')) {
      elements.push(
        <Typography key={i} variant="subtitle2" sx={{ mt: 1.5, mb: 0.5, fontWeight: 700, fontSize: '0.85rem' }}>
          {trimmed.slice(4)}
        </Typography>
      );
    } else if (trimmed.startsWith('## ')) {
      elements.push(
        <Typography key={i} variant="subtitle1" sx={{ mt: 2, mb: 0.5, fontWeight: 700, fontSize: '0.9rem' }}>
          {trimmed.slice(3)}
        </Typography>
      );
    } else if (trimmed.startsWith('# ')) {
      elements.push(
        <Typography key={i} variant="h6" sx={{ mt: 2, mb: 0.5, fontWeight: 700, fontSize: '0.95rem' }}>
          {trimmed.slice(2)}
        </Typography>
      );
    } else if (trimmed.startsWith('> ')) {
      elements.push(
        <Box
          key={i}
          sx={{
            borderLeft: '3px solid',
            borderColor: 'warning.main',
            pl: 1.5,
            py: 0.25,
            my: 0.5,
            bgcolor: 'action.hover',
            borderRadius: '0 4px 4px 0',
          }}
        >
          <Typography variant="caption">{trimmed.slice(2)}</Typography>
        </Box>
      );
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      elements.push(
        <Typography key={i} variant="body2" sx={{ pl: 2, fontSize: '0.8rem', lineHeight: 1.6 }}>
          {'• '}{formatInline(trimmed.slice(2))}
        </Typography>
      );
    } else if (/^\d+\.\s/.test(trimmed)) {
      const match = trimmed.match(/^(\d+)\.\s(.*)$/);
      if (match) {
        elements.push(
          <Typography key={i} variant="body2" sx={{ pl: 2, fontSize: '0.8rem', lineHeight: 1.6 }}>
            {match[1]}. {formatInline(match[2])}
          </Typography>
        );
      }
    } else if (trimmed === '') {
      elements.push(<Box key={i} sx={{ height: 4 }} />);
    } else {
      elements.push(
        <Typography key={i} variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.6 }}>
          {formatInline(trimmed)}
        </Typography>
      );
    }
  });

  return <>{elements}</>;
}

/**
 * Handle bold (**text**) and code (`text`) inline formatting
 */
function formatInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)/s);
    if (boldMatch) {
      if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>);
      parts.push(<strong key={key++}>{boldMatch[2]}</strong>);
      remaining = boldMatch[3];
      continue;
    }

    // Code: `text`
    const codeMatch = remaining.match(/^(.*?)`(.+?)`(.*)/s);
    if (codeMatch) {
      if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>);
      parts.push(
        <code key={key++} style={{ backgroundColor: 'rgba(0,0,0,0.08)', padding: '1px 4px', borderRadius: 3, fontSize: '0.75rem' }}>
          {codeMatch[2]}
        </code>
      );
      remaining = codeMatch[3];
      continue;
    }

    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }

  return <>{parts}</>;
}

const CADAIAnalysisPanel: React.FC<CADAIAnalysisPanelProps> = ({
  modelId,
  existingAnalysis,
  onAnalysisComplete,
}) => {
  const { t } = useTranslation();
  const [analysis, setAnalysis] = useState<string | null>(existingAnalysis);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAnalyze = async () => {
    if (!modelId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await analyzeCADModel(modelId);
      setAnalysis(result.analysis);
      onAnalysisComplete?.(result.analysis);
    } catch (err: any) {
      setError(err?.message || t('cad.ai.failed'));
    } finally {
      setLoading(false);
    }
  };

  // Update when existingAnalysis changes (model selection)
  React.useEffect(() => {
    setAnalysis(existingAnalysis);
    setError(null);
  }, [existingAnalysis, modelId]);

  if (!modelId) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          {t('cad.ai.selectModel')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          {t('cad.ai.title')}
        </Typography>
        <Button
          size="small"
          variant={analysis ? 'outlined' : 'contained'}
          startIcon={loading ? <CircularProgress size={16} /> : <AIIcon />}
          onClick={handleAnalyze}
          disabled={loading}
        >
          {loading ? t('cad.ai.analyzing') : analysis ? t('cad.ai.reanalyze') : t('cad.ai.start')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {analysis && (
        <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 400, overflow: 'auto' }}>
          {renderMarkdown(analysis)}
        </Paper>
      )}

      {!analysis && !loading && (
        <Typography variant="caption" color="text.secondary">
          {t('cad.ai.description')}
        </Typography>
      )}
    </Box>
  );
};

export default CADAIAnalysisPanel;
